/*
  File: src/components/store/FiltersSidebar.jsx
  Purpose: Presentational filter sidebar for product listings.
  Props: 
    - query, onQueryChange
    - minPrice, maxPrice, onMinPriceChange, onMaxPriceChange
    - categoriesTop (array), selectedCategoryId, onSelectCategory
    - subcategoryIds (array), onToggleSubcategory
    - vendors (array), vendorIds (array), onToggleVendor
    - onReset, error
*/
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { apiClient } from '../../auth/api';

const EXCLUDED_TEAM_IDS = new Set([5, 6]); // 5=San Antonio Spurs, 6=New York Knicks

/**
 * FiltersSidebar
 * Reusable sidebar component for product listing filters.
 * Keeps presentational concerns here and lifts state up via props.
 */
function FiltersSidebar({
  query,
  onQueryChange,
  minPrice,
  maxPrice,
  onMinPriceChange,
  onMaxPriceChange,
  categoriesTop = [],
  selectedCategoryId,
  onSelectCategory,
  subcategoryIds = [],
  onToggleSubcategory,
  vendors = [],
  vendorIds = [],
  onToggleVendor, // legacy, kept for compatibility (unused)
  onSelectVendor,
  onReset,
  error,
  onPlayerFilterChange,
  playerIds = [],
  onTeamFilterChange,
  teamIds = [],
  onSeriesFilterChange,
  seriesIds = [],
  onCharacterFilterChange,
  characterIds = [],
}) {
  const selectedCategory = (categoriesTop || []).find(c => c.id === selectedCategoryId) || null;
  const subcategories = selectedCategory ? (selectedCategory.subcategories || []) : [];

  const selectedVendorValue = Array.isArray(vendorIds) && vendorIds.length > 0 ? String(vendorIds[0]) : '';

  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const playersCacheRef = useRef(new Map()); // vendorId -> players array
  // Player selection is controlled by parent via playerIds

  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);

  const [series, setSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [showAllSeries, setShowAllSeries] = useState(false);

  const [characters, setCharacters] = useState([]);
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [showAllCharacters, setShowAllCharacters] = useState(false);
  const charactersCacheRef = useRef(new Map()); // seriesId -> characters array

  // Fetch players when vendor or team changes (debounced + cached)
  useEffect(() => {
    setPlayers([]);
    setShowAllPlayers(false);
    
    // Priority: if teams are selected, fetch players from all selected teams
    if (teamIds.length > 0) {
      let cancelled = false;
      const handle = setTimeout(async () => {
        setLoadingPlayers(true);
        try {
          const allPlayers = [];
          const playerIdSet = new Set(); // To avoid duplicates
          
          // Fetch players for each selected team
          for (const teamId of teamIds) {
            const cacheKey = `team_${teamId}`;
            let teamPlayers = playersCacheRef.current.get(cacheKey);
            
            if (!teamPlayers) {
              // Fetch from API if not cached
              const { data } = await apiClient.get(`/players/by-team/${teamId}`);
              teamPlayers = data.items || data || [];
              playersCacheRef.current.set(cacheKey, teamPlayers);
            }
            
            // Add players avoiding duplicates
            teamPlayers.forEach(player => {
              if (!playerIdSet.has(player.id)) {
                playerIdSet.add(player.id);
                allPlayers.push(player);
              }
            });
          }
          
          if (!cancelled) {
            setPlayers(allPlayers);
          }
        } catch (e) {
          if (!cancelled) setPlayers([]);
        } finally {
          if (!cancelled) setLoadingPlayers(false);
        }
      }, 250);

      return () => { cancelled = true; clearTimeout(handle); };
    }
    
    // Fallback: if vendor is selected but no team, use vendor-based endpoint
    if (!selectedVendorValue) return;
    const vendorObj = (vendors || []).find(v => String(v.id) === String(selectedVendorValue));
    if (!vendorObj) return;

    const cacheKey = `vendor_${vendorObj.id}`;
    const cached = playersCacheRef.current.get(cacheKey);
    if (cached && Array.isArray(cached)) {
      setPlayers(cached);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoadingPlayers(true);
      try {
        const { data } = await apiClient.get('/players/search', { params: { team: vendorObj.name, limit: 400, offset: 0 } });
        const items = data.items || [];
        if (!cancelled) {
          setPlayers(items);
          playersCacheRef.current.set(cacheKey, items);
        }
      } catch (e) {
        if (!cancelled) setPlayers([]);
      } finally {
        if (!cancelled) setLoadingPlayers(false);
      }
    }, 250);

    return () => { cancelled = true; clearTimeout(handle); };
  }, [selectedVendorValue, vendors, teamIds]);

  // Fetch teams on component mount
  useEffect(() => {
    let cancelled = false;
    setLoadingTeams(true);
    
    const fetchTeams = async () => {
      try {
        const { data } = await apiClient.get('/teams', { params: { limit: 50, offset: 0 } });
        if (!cancelled) {
          const items = (data.items || []).filter(t => !EXCLUDED_TEAM_IDS.has(Number(t?.id)));
          setTeams(items);
        }
      } catch (e) {
        if (!cancelled) setTeams([]);
      } finally {
        if (!cancelled) setLoadingTeams(false);
      }
    };

    fetchTeams();
    return () => { cancelled = true; };
  }, []);

  // Fetch series on component mount
  useEffect(() => {
    let cancelled = false;
    setLoadingSeries(true);
    
    const fetchSeries = async () => {
      try {
        const { data } = await apiClient.get('/series', { params: { limit: 50, offset: 0 } });
        if (!cancelled) {
          setSeries(data.items || []);
        }
      } catch (e) {
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setLoadingSeries(false);
      }
    };

    fetchSeries();
    return () => { cancelled = true; };
  }, []);

  // Fetch characters when series changes (debounced + cached)
  useEffect(() => {
    setCharacters([]);
    setShowAllCharacters(false);
    
    // Fetch characters for all selected series
    if (seriesIds.length > 0) {
      let cancelled = false;
      const handle = setTimeout(async () => {
        setLoadingCharacters(true);
        try {
          const allCharacters = [];
          const characterIdSet = new Set(); // To avoid duplicates
          
          // Fetch characters for each selected series
          for (const seriesId of seriesIds) {
            const cacheKey = `series_${seriesId}`;
            let seriesCharacters = charactersCacheRef.current.get(cacheKey);
            
            if (!seriesCharacters) {
              // Fetch from API if not cached
              const { data } = await apiClient.get(`/products/by-series/${seriesId}/characters-products`);
              // Extract unique characters from the response
              const charactersSet = new Set();
              const charactersArray = [];
              
              if (data && data.characters) {
                data.characters.forEach(item => {
                  const character = item.character;
                  if (character && !charactersSet.has(character.id)) {
                    charactersSet.add(character.id);
                    charactersArray.push(character);
                  }
                });
              }
              
              seriesCharacters = charactersArray;
              charactersCacheRef.current.set(cacheKey, seriesCharacters);
            }
            
            // Add characters avoiding duplicates
            seriesCharacters.forEach(character => {
              if (!characterIdSet.has(character.id)) {
                characterIdSet.add(character.id);
                allCharacters.push(character);
              }
            });
          }
          
          if (!cancelled) {
            setCharacters(allCharacters);
          }
        } catch (e) {
          if (!cancelled) setCharacters([]);
        } finally {
          if (!cancelled) setLoadingCharacters(false);
        }
      }, 250);

      return () => { cancelled = true; clearTimeout(handle); };
    }
  }, [seriesIds]);

  // No side-effect sync; parent owns playerIds

  return (
    <aside className="md:w-72 w-full sticky top-20 max-h-[calc(100vh-6rem)] rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)] flex flex-col">
      <div className="flex items-center justify-between p-5 pb-4 flex-shrink-0">
        <h2 className="text-sm font-bold tracking-wide text-white/90">Filters</h2>
        <button onClick={onReset} className="text-xs px-2 py-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition">Reset</button>
      </div>
      <div className="space-y-5 text-sm px-5 pb-5 overflow-y-auto flex-1 scrollbar-thin scrollbar-track-white/5 scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30">
        {error && <div className="text-red-400">Failed to load products.</div>}

        {/* Search */}
        <div>
          <div className="mb-2 text-white/80">Search</div>
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2">
            <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
            <input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search products, brands..."
              className="flex-1 bg-transparent outline-none text-white placeholder-white/60 text-sm"
            />
          </div>
        </div>

        {/* Price */}
        <div>
          <div className="mb-2 text-white/80">Price range</div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-xs text-white/60 mb-1">Min</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70">$</span>
                <input
                  type="number"
                  min={0}
                  value={minPrice}
                  onChange={(e) => onMinPriceChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-white/60 mb-1">Max</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70">$</span>
                <input
                  type="number"
                  min={0}
                  value={maxPrice}
                  onChange={(e) => onMaxPriceChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vendors (single select) */}
        <div>
          <div className="mb-2 text-white/80">Vendors</div>
          <SearchSelect
            options={vendors}
            value={selectedVendorValue}
            onChange={onSelectVendor}
            placeholder="All vendors"
            displayKey="name"
            valueKey="id"
          />
        </div>

        {/* Categories */}
        <div>
          <div className="mb-2 text-white/80">Category</div>
          <div className="flex flex-wrap gap-2">
            {(categoriesTop || []).map((cat) => {
              const active = selectedCategoryId === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onSelectCategory(active ? null : cat.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100/90' : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'}`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Subcategories */}
        {selectedCategory && (
          <div>
            <div className="mb-2 text-white/80">Subcategories</div>
            {subcategories.length === 0 ? (
              <div className="text-xs text-white/60">No subcategories</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {subcategories.map((sc) => {
                  const active = subcategoryIds.includes(sc.id);
                  return (
                    <button
                      key={sc.id}
                      type="button"
                      onClick={() => onToggleSubcategory(sc.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? 'border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-100/90' : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'}`}
                    >
                      {sc.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Teams */}
        <div>
          <div className="mb-2 text-white/80 flex items-center gap-2">Teams {loadingTeams && <span className="text-xs text-white/50">Loading...</span>}</div>
          <div className="flex flex-col gap-2">
            {(showAllTeams ? teams : teams.slice(0, 10)).map(team => {
              const isTeamSelected = teamIds.includes(team.id);
              const teamPlayers = players.filter(player => 
                // Players are shown when this team is selected
                isTeamSelected
              );
              
              return (
                <div key={team.id} className="border border-white/10 rounded-lg bg-white/5">
                  <label className="flex items-center justify-between min-h-[48px] px-3 py-2 hover:bg-white/10 transition cursor-pointer rounded-lg" style={{ minWidth: 0 }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="relative flex items-center">
                        <input
                          type="radio"
                          name="team-filter"
                          checked={isTeamSelected}
                          onChange={e => {
                            if (!onTeamFilterChange) return;
                            if (e.target.checked) {
                              onTeamFilterChange([team.id]);
                            } else {
                              onTeamFilterChange([]);
                            }
                          }}
                          onClick={e => {
                            if (!onTeamFilterChange) return;
                            if (isTeamSelected) {
                              e.preventDefault();
                              onTeamFilterChange([]);
                            }
                          }}
                          className="peer appearance-none w-5 h-5 rounded-full border-2 border-white/20 bg-white/10 checked:bg-emerald-500 checked:border-emerald-500 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                          style={{ minWidth: 20, minHeight: 20 }}
                        />
                        <span className="pointer-events-none absolute left-0 top-0 w-5 h-5 flex items-center justify-center">
                          <div className="opacity-0 peer-checked:opacity-100 w-2 h-2 bg-white rounded-full transition-opacity duration-150"></div>
                        </span>
                      </span>
                      <img src={team.logo_url} alt={team.display_name} className="w-7 h-7 object-cover rounded-full border border-white/10 flex-shrink-0" />
                      <span className="text-white text-sm truncate">{team.display_name}</span>
                    </div>
                  </label>
                  
                  {/* Players list for selected team */}
                  {isTeamSelected && teamPlayers.length > 0 && (
                    <div className="px-3 pb-3">
                      <div className="text-xs text-white/60 mb-2 ml-7">Players {loadingPlayers && <span className="text-white/50">Loading...</span>}</div>
                      <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-track-white/5 scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30">
                        <div className="flex flex-col gap-1">
                          {teamPlayers.map(player => (
                            <label key={player.id} className="flex items-center gap-2 min-h-[36px] px-2 py-1 ml-7 bg-white/5 rounded transition hover:bg-white/10 cursor-pointer" style={{ minWidth: 0 }}>
                              <span className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={playerIds.includes(player.id)}
                                  onChange={e => {
                                    if (!onPlayerFilterChange) return;
                                    if (e.target.checked) {
                                      onPlayerFilterChange([...playerIds, player.id]);
                                    } else {
                                      onPlayerFilterChange(playerIds.filter(id => id !== player.id));
                                    }
                                  }}
                                  className="peer appearance-none w-4 h-4 rounded border-2 border-white/20 bg-white/10 checked:bg-emerald-500 checked:border-emerald-500 transition-all duration-150 focus:outline-none"
                                />
                                <span className="pointer-events-none absolute left-0 top-0 w-4 h-4 flex items-center justify-center">
                                  <svg className="opacity-0 peer-checked:opacity-100 w-3 h-3 text-white transition-opacity duration-150" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M6 10l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </span>
                              </span>
                              <img src={player.image_url} alt={player.display_name} className="w-5 h-5 object-cover rounded-full border border-white/10 flex-shrink-0" />
                              <span className="text-white text-xs truncate">{player.display_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {teams.length > 10 && (
            <div className="mt-2">
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/10 hover:bg-white/20 transition"
                onClick={() => setShowAllTeams(v => !v)}
              >
                {showAllTeams ? 'Show less' : `Show all (${teams.length})`}
              </button>
            </div>
          )}
        </div>


        {/* Series */}
        <div>
          <div className="mb-2 text-white/80 flex items-center gap-2">Series {loadingSeries && <span className="text-xs text-white/50">Loading...</span>}</div>
          <div className="flex flex-col gap-2">
            {(showAllSeries ? series : series.slice(0, 10)).map(seriesItem => (
              <label key={seriesItem.id} className="flex items-center justify-between min-h-[48px] px-3 py-2 bg-white/5 rounded transition hover:shadow-md hover:bg-white/10 w-full" style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={seriesIds.includes(seriesItem.id)}
                      onChange={e => {
                        if (!onSeriesFilterChange) return;
                        if (e.target.checked) {
                          onSeriesFilterChange([...seriesIds, seriesItem.id]);
                        } else {
                          onSeriesFilterChange(seriesIds.filter(id => id !== seriesItem.id));
                        }
                      }}
                      className="peer appearance-none w-5 h-5 rounded-md border-2 border-white/20 bg-white/10 checked:bg-emerald-500 checked:border-emerald-500 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                      style={{ minWidth: 20, minHeight: 20 }}
                    />
                    <span className="pointer-events-none absolute left-0 top-0 w-5 h-5 flex items-center justify-center">
                      <svg className="opacity-0 peer-checked:opacity-100 w-4 h-4 text-white transition-opacity duration-150" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 10l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </span>
                  <img src={seriesItem.thumbnail_url} alt={seriesItem.display_title} className="w-7 h-7 object-cover rounded border border-white/10 flex-shrink-0" />
                  <span className="text-white text-sm truncate">{seriesItem.display_title}</span>
                </div>
              </label>
            ))}
          </div>
          {series.length > 10 && (
            <div className="mt-2">
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/10 hover:bg-white/20 transition"
                onClick={() => setShowAllSeries(v => !v)}
              >
                {showAllSeries ? 'Show less' : `Show all (${series.length})`}
              </button>
            </div>
          )}
        </div>

        {/* Characters (checkboxes) */}
        {seriesIds.length > 0 && characters.length > 0 && (
          <div>
            <div className="mb-2 text-white/80 flex items-center gap-2">Characters {loadingCharacters && <span className="text-xs text-white/50">Loading...</span>}</div>
            <div className="flex flex-col gap-2">
              {(showAllCharacters ? characters : characters.slice(0, 20)).map(character => (
                <label key={character.id} className="flex items-center justify-between min-h-[48px] px-3 py-2 bg-white/5 rounded transition hover:shadow-md hover:bg-white/10 w-full" style={{ minWidth: 0 }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={characterIds.includes(character.id)}
                        onChange={e => {
                          if (!onCharacterFilterChange) return;
                          if (e.target.checked) {
                            onCharacterFilterChange([...characterIds, character.id]);
                          } else {
                            onCharacterFilterChange(characterIds.filter(id => id !== character.id));
                          }
                        }}
                        className="peer appearance-none w-5 h-5 rounded-md border-2 border-white/20 bg-white/10 checked:bg-emerald-500 checked:border-emerald-500 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                        style={{ minWidth: 20, minHeight: 20 }}
                      />
                      <span className="pointer-events-none absolute left-0 top-0 w-5 h-5 flex items-center justify-center">
                        <svg className="opacity-0 peer-checked:opacity-100 w-4 h-4 text-white transition-opacity duration-150" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 10l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </span>
                    <img src={character.image_url} alt={character.name} className="w-7 h-7 object-cover rounded border border-white/10 flex-shrink-0" />
                    <span className="text-white text-sm truncate">{character.name}</span>
                  </div>
                </label>
              ))}
            </div>
            {characters.length > 20 && (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/10 hover:bg-white/20 transition"
                  onClick={() => setShowAllCharacters(v => !v)}
                >
                  {showAllCharacters ? 'Show less' : `Show all (${characters.length})`}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </aside>
  );
}

function SearchSelect({ options, value, onChange, placeholder, displayKey = 'name', valueKey = 'id' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectedOption = options.find(opt => String(opt[valueKey]) === String(value)) || null;
  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter(opt => String(opt[displayKey]).toLowerCase().includes(term));
  }, [options, searchTerm, displayKey]);
  return (
    <div className="relative">
      <div
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="truncate text-white">
          {selectedOption ? selectedOption[displayKey] : <span className="text-white/50">{placeholder}</span>}
        </div>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
          <div className="p-2 border-b border-white/10">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            <div
              className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${value === '' ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
              onClick={() => { onChange(''); setIsOpen(false); setSearchTerm(''); }}
            >
              All vendors
            </div>
            {filteredOptions.map(opt => (
              <div
                key={opt[valueKey]}
                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${String(opt[valueKey]) === String(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                onClick={() => { onChange(opt[valueKey]); setIsOpen(false); setSearchTerm(''); }}
              >
                {opt[displayKey]}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-white/60">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FiltersSidebar;
